// ============================================================================
// THEMES DASHBOARD COMPONENT
// ============================================================================
// Displays user's saved wedding themes from DreamWeddingGenerator and MoodboardMatcher
// Provides grid view for browsing and detailed modal view for exploring themes
// Includes AI Color Magic integration for color exploration
// ============================================================================

import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import BackgroundIcons from '../components/BackgroundIcons';
import AIMagicButton from '../components/AIMagicButton';
import AIColorMagicLoader from '../components/AIColorMagicLoader';
import AsyncColorName from '../components/AsyncColorName';
import NotificationModal from '../components/NotificationModal';

// ============================================================================
// UTILITY IMPORTS
// ============================================================================

// Modal rendering helper - shared AI Color Magic modal across all pages
import { renderAIColorMagicModal, renderRitualModal } from '../utils/modalRenderers';
import { fetchSavedRitualIds, saveRitualToWeddingPlan } from '../utils/ritualHelpers';

// Color utilities for hex/RGB conversion, interpolation, and color analysis
import { 
  hexToRgb, 
  rgbToHsl, 
  interpolateRgb,
  parseColors, 
  extractHexFromColor, 
  getContrastColor,
  getColorName
} from '../utils/colorHelpers';

// AI-related utilities for color magic and wedding applications
import { 
  triggerAIMagic, 
  getSmartWeddingApplications, 
  getDisplayApplications 
} from '../utils/aiHelpers';

// Style utilities for icons and categories
import { categories } from '../utils/styleHelpers';

// Theme rendering helpers - shared with DreamWedding and Moodboard for consistency
import { 
  renderColorOrbs, 
  renderColorCodes, 
  renderColorFlow,
  renderStyleKeywords,
  renderElements,
  renderRitualSection
} from '../utils/themeRenderers';

const ThemesDashboard = () => {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================
  
  // Saved themes data from backend
  const [savedThemes, setSavedThemes] = useState([]); // Array of saved theme objects
  const [loading, setLoading] = useState(true); // Loading state during initial fetch
  const [error, setError] = useState(null); // Error message if fetch fails
  
  // Theme detail modal state
  const [selectedTheme, setSelectedTheme] = useState(null); // Currently selected theme for modal view
  const [showThemeModal, setShowThemeModal] = useState(false); // Modal visibility toggle
  
  // AI Color Magic modal state (matches DreamWedding/Moodboard pattern)
  const [colorModal, setColorModal] = useState({ 
    show: false, // Modal visibility
    color: '', // Selected color hex code
    colorName: '', // Human-readable color name
    applications: [], // Wedding applications for the color
    selectedCategory: 'decor' // Selected category: decor, flowers, attire, etc.
  });

  // Notification state for ritual save feedback
  const [notification, setNotification] = useState({ show: false, title: '', message: '', type: 'success' });

  // Ritual modal state
  const [ritualModal, setRitualModal] = useState({
    show: false,
    ritual: null
  });
  const [savedRitualIds, setSavedRitualIds] = useState(new Set());

  useEffect(() => { fetchSavedRitualIds().then(setSavedRitualIds); }, []);
  useEffect(() => { if (ritualModal.show) fetchSavedRitualIds().then(setSavedRitualIds); }, [ritualModal.show]);

  const handleSaveRitual = async (ritual) => {
    const result = await saveRitualToWeddingPlan(ritual);
    if (result.success) setSavedRitualIds(prev => new Set([...prev, ritual.id]));
    setNotification(result.notification);
  };

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    themeId: null,
    themeName: ''
  });

  // ============================================================================
  // DATABASE-SPECIFIC HELPER FUNCTIONS
  // ============================================================================
  // These functions handle database field parsing and stored format extraction
  // Kept inline as they are specific to ThemesDashboard's data structure
  // ============================================================================

  // Helper function to safely parse arrays from database fields
  // Handles JSON strings, comma-separated strings, and arrays
  const parseArray = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    
    try {
      return JSON.parse(field);
    } catch (error) {
      // Handle comma-separated string format
      if (typeof field === 'string') {
        return field.split(',').map(item => item.trim()).filter(item => item.length > 0);
      }
      return [];
    }
  };

  // Helper function to extract color name from stored "Name (#HEX)" format
  // Themes are saved with AI-generated poetic names in "Name (#HEX)" format
  // This function extracts just the name part for display
  const extractNameFromColor = (colorString) => {
    if (!colorString) return 'Unknown Color';
    
    // If it's just a hex code, return it as is
    if (colorString.startsWith('#')) {
      return colorString;
    }
    
    // Extract name from "Name (#HEX)" format
    const nameMatch = colorString.match(/^(.+?)\s*\(#[0-9A-Fa-f]{6}\)$/);
    if (nameMatch) {
      return nameMatch[1].trim();
    }
    
    // Return the original string if no pattern matches
    return colorString;
  };

  // ============================================================================
  // AI COLOR MAGIC WRAPPER FUNCTIONS
  // ============================================================================
  // These wrappers pass local state to imported AI helper functions
  // Matches DreamWedding/Moodboard pattern for consistency
  // ============================================================================

  // Trigger AI Magic to get wedding applications for selected color
  // Uses the theme's original AI model for consistency
  const wrappedTriggerAIMagic = async () => {
    const modelToUse = selectedTheme?.ai_model || 'auto';
    await triggerAIMagic(colorModal, setColorModal, modelToUse, apiService);
  };

  // Get display applications for selected color in modal
  const wrappedGetDisplayApplications = () => {
    return getDisplayApplications(colorModal);
  };

  // ============================================================================
  // THEME-SPECIFIC RENDERING WRAPPER
  // ============================================================================
  // Custom color orbs renderer that uses stored color names instead of API
  // ThemesDashboard displays saved themes with AI-generated names already stored
  // ============================================================================

  // Wrapper for color orbs that uses stored color names instead of AsyncColorName
  // This ensures we display the exact names that were generated when theme was created
  const wrappedRenderColorOrbs = (colors, sourceModel, style) => {
    // Custom implementation for ThemesDashboard that uses stored names
    const parsedColors = parseColors(colors);
    
    return (
      <div className="flex flex-wrap justify-center gap-8 mb-8">
        {parsedColors.map((color, index) => (
          <div 
            key={index} 
            className="group/orb transform hover:scale-125 transition-all duration-700 cursor-pointer"
            onClick={async () => {
              const selectedColor = extractHexFromColor(color);
              const colorName = extractNameFromColor(color);
              
              setColorModal({
                show: true,
                color: selectedColor,
                position: 50,
                colorName: colorName,
                applications: ['SHOW_AI_BUTTON'],
                selectedCategory: 'decor'
              });
            }}
          >
            <div className="relative">
              <div 
                className="absolute inset-0 rounded-full animate-ping opacity-30"
                style={{ 
                  backgroundColor: extractHexFromColor(color),
                  transform: 'scale(1.4)',
                  animationDuration: '3s',
                  animationDelay: `${index * 0.5}s`
                }}
              ></div>
              
              <div 
                className="w-24 h-24 rounded-full shadow-2xl border-4 border-white relative overflow-hidden group-hover/orb:rotate-12 transition-all duration-700"
                style={{ 
                  backgroundColor: extractHexFromColor(color),
                  boxShadow: `0 15px 40px ${extractHexFromColor(color)}60, inset 0 0 20px rgba(255,255,255,0.3)`
                }}
              >
                <div className="absolute top-2 left-2 w-6 h-6 bg-white opacity-40 rounded-full blur-sm"></div>
                <div className="absolute bottom-2 right-2 w-4 h-4 bg-black opacity-20 rounded-full blur-sm"></div>
              </div>
              
              <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                <div 
                  className="px-4 py-2 rounded-full shadow-lg border-2 border-white font-bold text-sm capitalize transform group-hover/orb:scale-110 transition-all duration-300"
                  style={{ 
                    backgroundColor: extractHexFromColor(color),
                    color: getContrastColor(extractHexFromColor(color)),
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  {extractNameFromColor(color)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============================================================================
  // BACKGROUND ICONS CONFIGURATION
  // ============================================================================
  
  // Background icons for VowAI theme - floating decorative elements
  const dashboardIcons = [
    { emoji: '💍', size: '4xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' },
    { emoji: '🎨', size: '4xl', opacity: '15' },
    { emoji: '🌸', size: '4xl', opacity: '15' }
  ];

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  // Fetch saved themes on component mount
  // ============================================================================

  useEffect(() => {
    fetchSavedThemes();
  }, []);

  // Fetch all saved themes from backend
  // Themes are created by DreamWeddingGenerator and MoodboardMatcher
  const fetchSavedThemes = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching saved themes...');
      // Pass limit=1000 to fetch all themes (backend default is only 20)
      const response = await apiService.savedThemes.getAll({ limit: 1000 });
      console.log('📊 Themes API Response:', response);
      
      // API returns axios response with data: { success: true, themes: [...] }
      const themes = response?.data?.themes || [];
      console.log('✅ Processed themes:', themes);
      setSavedThemes(themes);
    } catch (error) {
      console.error('❌ Error fetching themes:', error);
      setError('Failed to load themes');
      setSavedThemes([]); // Ensure array on error
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // THEME MANAGEMENT FUNCTIONS
  // ============================================================================

  // Delete a saved theme from backend and update local state
  const deleteTheme = async (themeId) => {
    console.log('🗑️ Deleting theme:', themeId);
    try {
      await apiService.savedThemes.delete(themeId);
      setSavedThemes(prev => prev.filter(theme => theme.id !== themeId));
    } catch (error) {
      console.error('Error deleting theme:', error);
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    await deleteTheme(deleteConfirm.themeId);
    setDeleteConfirm({ show: false, themeId: null, themeName: '' });
  };

  // Open theme detail modal with selected theme
  const viewThemeDetails = (theme) => {
    setSelectedTheme(theme);
    setShowThemeModal(true);
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  // Show loading animation while fetching themes from backend
  // ============================================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-rose-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce">
            <span className="text-4xl animate-pulse">🎨</span>
          </div>
          <p className="text-2xl font-serif font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
            Loading your beautiful themes...
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ERROR STATE
  // ============================================================================
  // Show error message if theme fetching fails
  // ============================================================================

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-red-400 to-rose-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
            <span className="text-4xl">💔</span>
          </div>
          <p className="text-xl text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN DASHBOARD UI
  // ============================================================================
  // Two main views: Grid View (theme cards) and Modal View (full details)
  // ============================================================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={dashboardIcons} />
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* ================================================================ */}
        {/* DASHBOARD HEADER                                                 */}
        {/* ================================================================ */}
        {/* Page title, theme count, and navigation                         */}
        {/* ================================================================ */}
        
        <div className="text-center mb-16 relative">
          
          {/* Back to Dashboard Button */}
          <div className="absolute top-0 left-0">
            <a 
              href="/dashboard"
              className="group relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-3 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            >
              <span className="mr-2 text-xl group-hover:animate-bounce">←</span>
              Back to Dashboard
              {/* VowAI signature glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
            </a>
          </div>
          
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-4xl animate-pulse">🎨</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 tracking-wide animate-fadeInUp">
            Your Dream Themes
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">💖</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>
          
          <p className="text-2xl text-gray-700 font-light italic animate-fadeInUp delay-300">
            Your collection of magical wedding inspirations ✨
          </p>
          
          <div className="mt-8">
            <span className="px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full font-bold text-lg shadow-xl animate-pulse">
              {Array.isArray(savedThemes) ? savedThemes.length : 0} Beautiful Themes Saved
            </span>
          </div>
        </div>

        {/* ================================================================ */}
        {/* THEMES GRID VIEW                                                 */}
        {/* ================================================================ */}
        {/* Displays saved themes as cards with preview information         */}
        {/* Shows empty state if no themes saved                            */}
        {/* Each card shows: colors, style keywords, elements preview       */}
        {/* ================================================================ */}
        
        {!Array.isArray(savedThemes) || savedThemes.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-32 h-32 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl animate-bounce">
              <span className="text-6xl animate-pulse">🌟</span>
            </div>
            <h3 className="text-4xl font-serif font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
              Start Your Dream Collection
            </h3>
            <p className="text-xl text-gray-600 mb-10 font-light max-w-2xl mx-auto">
              Create your first magical wedding theme and watch your dream wedding come to life
            </p>
            <a 
              href="/wedding-blueprint/dream-wedding-generator"
              className="inline-flex items-center px-10 py-4 bg-gradient-to-r from-rose-400 to-pink-500 text-white rounded-2xl font-bold text-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <span className="mr-3 text-2xl">✨</span>
              Create Your First Theme
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.isArray(savedThemes) && savedThemes.map((theme, index) => (
              <div 
                key={theme.id} 
                className="group relative bg-gradient-to-br from-white via-rose-50 to-pink-50 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-white/50 hover:shadow-3xl transform hover:scale-105 hover:-rotate-1 transition-all duration-700 overflow-hidden animate-fadeInUp"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Magical gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-rose-100/40 via-pink-100/30 to-purple-100/40 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                {/* Floating sparkles */}
                <div className="absolute top-4 right-4 text-gold-300 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-500">✨</div>
                <div className="absolute bottom-4 left-4 text-rose-300 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ animationDelay: '0.5s' }}>💫</div>
                
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-rose-400/20 to-pink-500/20 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-700 -z-10"></div>
                
                <div className="relative z-10 p-8">
                  {/* Enhanced Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center flex-1">
                      <div className="w-16 h-16 bg-gradient-to-br from-purple-400 via-pink-500 to-rose-500 rounded-3xl flex items-center justify-center mr-5 shadow-2xl group-hover:scale-110 group-hover:rotate-12 transition-all duration-700 relative overflow-hidden">
                        {/* Inner shine */}
                        <div className="absolute top-1 left-1 w-4 h-4 bg-white opacity-40 rounded-full blur-sm"></div>
                        <span className="text-3xl group-hover:animate-bounce relative z-10">🎨</span>
                      </div>
                      <div className="flex-1">
                        <h3 
                          onClick={() => viewThemeDetails(theme)}
                          className="text-2xl font-serif font-bold text-gray-800 hover:text-transparent hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-600 hover:bg-clip-text transition-all duration-500 leading-tight cursor-pointer"
                        >
                          {theme.theme_name}
                        </h3>
                        {theme.ai_model && (
                          <div className="mt-2">
                            <span className="text-xs px-3 py-1 bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 text-purple-700 rounded-full font-bold border border-purple-200 shadow-sm">
                              ✨ {theme.ai_model.toUpperCase()} Generated
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setDeleteConfirm({ show: true, themeId: theme.id, themeName: theme.theme_name })}
                      className="text-gray-400 hover:text-red-500 transition-colors p-3 rounded-full hover:bg-red-50 hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl"
                      title="Delete theme"
                    >
                      <span className="text-xl">🗑️</span>
                    </button>
                  </div>

                  {/* Enhanced Description */}
                  <div className="mb-6">
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 group-hover:text-gray-700 transition-colors duration-300 bg-white/50 p-4 rounded-2xl border border-white/80 shadow-inner">
                      {theme.description}
                    </p>
                  </div>

                  {/* Enhanced Colors Preview */}
                  {theme.colors && parseArray(theme.colors).length > 0 && (
                    <div className="mb-5">
                      <div className="flex items-center mb-3">
                        <span className="text-sm font-bold text-gray-500 flex items-center">
                          <span className="mr-2 text-lg group-hover:animate-bounce">🎨</span> Colors
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {parseColors(theme.colors).slice(0, 4).map((color, colorIndex) => (
                          <div key={colorIndex} className="group/color">
                            <div className="flex items-center bg-white/80 px-3 py-2 rounded-xl border border-rose-200/50 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                              <div 
                                className="w-4 h-4 rounded-full mr-2 border-2 border-white shadow-sm"
                                style={{ backgroundColor: extractHexFromColor(color) }}
                              ></div>
                              <span className="text-xs font-semibold text-rose-700 capitalize">{extractNameFromColor(color)}</span>
                            </div>
                          </div>
                        ))}
                        {parseArray(theme.colors).length > 4 && (
                          <div className="flex items-center bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-2 rounded-xl border border-gray-300 shadow-sm">
                            <span className="text-xs font-bold text-gray-600">
                              +{parseArray(theme.colors).length - 4} more
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Enhanced Style Keywords */}
                  {theme.style_keywords && parseArray(theme.style_keywords).length > 0 && (
                    <div className="mb-5">
                      <div className="flex items-center mb-3">
                        <span className="text-sm font-bold text-gray-500 flex items-center">
                          <span className="mr-2 text-lg group-hover:animate-bounce">✨</span> Style
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {parseArray(theme.style_keywords).slice(0, 3).map((keyword, keywordIndex) => (
                          <span key={keywordIndex} className="px-3 py-1 bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-700 rounded-xl text-xs font-bold border border-purple-200/50 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300">
                            {keyword}
                          </span>
                        ))}
                        {parseArray(theme.style_keywords).length > 3 && (
                          <span className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 rounded-xl text-xs font-bold border border-gray-300">
                            +{parseArray(theme.style_keywords).length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Enhanced Elements */}
                  {theme.elements && parseArray(theme.elements).length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center mb-3">
                        <span className="text-sm font-bold text-gray-500 flex items-center">
                          <span className="mr-2 text-lg group-hover:animate-bounce">🌸</span> Elements
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {parseArray(theme.elements).slice(0, 3).map((element, elementIndex) => (
                          <span key={elementIndex} className="px-3 py-1 bg-gradient-to-r from-gold-100 to-yellow-100 text-gold-700 rounded-xl text-xs font-bold border border-gold-200/50 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300">
                            {element}
                          </span>
                        ))}
                        {parseArray(theme.elements).length > 3 && (
                          <span className="px-3 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 rounded-xl text-xs font-bold border border-gray-300">
                            +{parseArray(theme.elements).length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Enhanced Date */}
                  <div className="text-xs text-gray-500 mb-6 font-medium bg-white/30 px-3 py-2 rounded-lg border border-white/50">
                    <span className="mr-2">📅</span>
                    Created {new Date(theme.created_at).toLocaleDateString()}
                  </div>

                  {/* Enhanced Action Button */}
                  <button 
                    onClick={() => viewThemeDetails(theme)}
                    className="w-full relative overflow-hidden bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 text-white py-4 px-6 rounded-2xl font-bold text-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 animate-pulse group/btn"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      <span className="mr-2 text-xl group-hover/btn:animate-bounce">✨</span>
                      View Full Details
                    </span>
                    {/* VowAI signature glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-gold-400/20 to-rose-400/20 opacity-0 hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* THEME DETAILS MODAL                                              */}
      {/* ================================================================ */}
      {/* Full-screen modal showing complete theme details                */}
      {/* Includes: description, colors (orbs, codes, flow), keywords,    */}
      {/* elements, inspiration text, and action buttons                  */}
      {/* Uses helper functions for consistent rendering                  */}
      {/* ================================================================ */}
      
      {showThemeModal && selectedTheme && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-rose-200/50 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            
            {/* Floating decorations */}
            <div className="absolute top-4 left-4 text-gold-300 animate-pulse opacity-70">✨</div>
            <div className="absolute top-8 right-8 text-rose-300 animate-pulse opacity-70" style={{ animationDelay: '0.5s' }}>💫</div>
            <div className="absolute bottom-8 left-8 text-purple-300 animate-pulse opacity-70" style={{ animationDelay: '1s' }}>🌟</div>
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 p-8 rounded-t-3xl relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
              
              <button
                onClick={() => setShowThemeModal(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl font-bold hover:rotate-90 transition-all duration-300 z-10"
              >
                ×
              </button>
              
              <div className="text-center relative z-10">
                <div className="text-5xl mb-4 animate-bounce">🎨</div>
                <h2 className="text-4xl font-serif font-bold text-white mb-3 tracking-wide">{selectedTheme.theme_name}</h2>
                <div className="flex items-center justify-center mb-2">
                  <div className="w-16 h-1 bg-white/30 rounded-full"></div>
                  <div className="mx-3 text-3xl animate-bounce">💖</div>
                  <div className="w-16 h-1 bg-white/30 rounded-full"></div>
                </div>
                <p className="text-white/90 text-lg font-light">
                  Created {new Date(selectedTheme.created_at).toLocaleDateString()} 
                  {selectedTheme.ai_model && (
                    <span className="ml-2 px-3 py-1 bg-white/20 rounded-full text-sm font-semibold">
                      {selectedTheme.ai_model.toUpperCase()} Generated
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-10 space-y-10 relative z-10">
              
              {/* Description */}
              <div className="group">
                <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300">
                  <span className="mr-3 text-3xl animate-bounce">📝</span> Theme Description
                </h3>
                <div className="bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 p-6 rounded-3xl border-2 border-rose-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                  <p className="text-gray-700 leading-relaxed text-lg font-light">
                    {selectedTheme.description}
                  </p>
                </div>
              </div>

              {/* Inspiration Text */}
              {selectedTheme.inspiration_text && (
                <div className="group">
                  <h3 className="text-2xl font-serif font-bold text-gray-800 mb-4 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-gold-600 group-hover:to-yellow-600 group-hover:bg-clip-text transition-all duration-300">
                    <span className="mr-3 text-3xl animate-bounce">💡</span> Original Inspiration
                  </h3>
                  <div className="bg-gradient-to-br from-gold-50 via-yellow-25 to-champagne-50 p-6 rounded-3xl border-2 border-gold-200/50 shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden">
                    <p className="text-gray-700 leading-relaxed text-lg font-light italic">
                      "{selectedTheme.inspiration_text}"
                    </p>
                  </div>
                </div>
              )}

              {/* Colors */}
              {selectedTheme.colors && parseArray(selectedTheme.colors).length > 0 && (
                <div className="group">
                  <h3 className="text-2xl font-serif font-bold text-gray-800 mb-6 flex items-center group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-pink-600 group-hover:bg-clip-text transition-all duration-300">
                    <span className="mr-3 text-3xl animate-bounce">🎨</span> Color Palette
                  </h3>
                  
                  {/* Spectacular Color Display */}
                  <div className="bg-white p-8 rounded-3xl shadow-2xl border-2 border-gray-100 relative overflow-hidden">
                    {/* Animated gradient background */}
                    <div className="absolute inset-0 opacity-10">
                      <div 
                        className="absolute inset-0 animate-pulse"
                        style={{
                          background: `linear-gradient(45deg, ${parseColors(selectedTheme.colors).map(color => extractHexFromColor(color) + '20').join(', ')})`
                        }}
                      ></div>
                    </div>
                    
                    <div className="relative z-10">
                      {/* Floating Color Orbs - Uses stored color names from theme */}
                      {wrappedRenderColorOrbs(selectedTheme.colors, selectedTheme.ai_model, 'individual')}
                      
                      {/* Professional Color Codes - Helper function from themeRenderers */}
                      {renderColorCodes(selectedTheme.colors)}
                      
                      {/* Color Flow Visualization - Interactive gradient with helper function */}
                      {renderColorFlow(selectedTheme.colors, selectedTheme.ai_model, setColorModal)}
                    </div>
                  </div>
                </div>
              )}

              {/* Style Keywords - Helper function from themeRenderers */}
              {selectedTheme.style_keywords && parseArray(selectedTheme.style_keywords).length > 0 && renderStyleKeywords(selectedTheme.style_keywords, 'individual')}

              {/* Wedding Elements - Helper function from themeRenderers */}
              {selectedTheme.elements && parseArray(selectedTheme.elements).length > 0 && renderElements(selectedTheme.elements, 'individual')}

              {/* Ritual Suggestions - Generated on-the-fly */}
              {renderRitualSection(selectedTheme.suggested_rituals, selectedTheme.theme_name, setRitualModal)}

              {/* Action Buttons */}
              <div className="flex gap-6 pt-8 border-t-2 border-gradient-to-r from-rose-200 to-pink-200">
                <button 
                  onClick={() => setShowThemeModal(false)}
                  className="flex-1 bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 py-4 px-8 rounded-2xl font-bold hover:from-gray-300 hover:to-gray-400 hover:shadow-xl transform hover:scale-105 transition-all duration-300 group"
                >
                  <span className="mr-2 group-hover:animate-bounce">👋</span>
                  Close
                </button>
                <button 
                  onClick={() => {
                    window.location.href = '/wedding-blueprint/dream-wedding-generator';
                  }}
                  className="flex-1 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 text-white py-4 px-8 rounded-2xl font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  <span className="relative z-10 flex items-center justify-center">
                    <span className="mr-2 text-xl animate-bounce">🎨</span>
                    Create Similar Theme
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* AI COLOR MAGIC MODAL                                             */}
      {/* ================================================================ */}
      {/* Shared modal component from utils/modalRenderers                */}
      {/* Shows color details, specifications, and AI-generated           */}
      {/* wedding applications when user clicks on colors                 */}
      {/* Consistent across DreamWedding, Moodboard, and ThemesDashboard  */}
      {/* ================================================================ */}
      
      {renderAIColorMagicModal(
        colorModal,
        setColorModal,
        wrappedGetDisplayApplications,
        wrappedTriggerAIMagic,
        hexToRgb,
        rgbToHsl,
        categories,
        AIMagicButton,
        AIColorMagicLoader
      )}

      {/* Ritual Details Modal */}
      {renderRitualModal(ritualModal, setRitualModal, null, null, savedRitualIds, handleSaveRitual)}

      {/* Delete Confirmation Modal */}
      <NotificationModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, themeId: null, themeName: '' })}
        type="warning"
        title="Delete Theme?"
        message={`Are you sure you want to delete "${deleteConfirm.themeName}"? This action cannot be undone.`}
        isConfirmation={true}
        onConfirm={handleDeleteConfirm}
        confirmText="Yes, Delete"
        cancelText="Cancel"
      />
      <NotificationModal
        isOpen={notification.show}
        onClose={() => setNotification({ show: false, title: '', message: '', type: 'success' })}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default ThemesDashboard;
